import type {
  TransferState,
  ViewerAdmissionState,
} from "@aasaan-share/ui-contract";
import type { ViewerUiModel } from "./ui/models.ts";

const PHOTO_A = "00000000-0000-4000-8000-000000000020";
const PHOTO_B = "00000000-0000-4000-8000-000000000022";
const PHOTO_C = "00000000-0000-4000-8000-000000000023";
const VIDEO = "00000000-0000-4000-8000-000000000021";
const SHARE_A = "00000000-0000-4000-8000-000000000010";
const SHARE_B = "00000000-0000-4000-8000-000000000011";

const shares = [
  {
    access: "download" as const,
    canContribute: true,
    collaboratorFilter: "all",
    collaborators: [
      {
        avatarUrl: "/assets/fixture-unsplash.jpg",
        count: 1,
        displayName: "Anika",
        recipientId: "00000000-0000-4000-8000-000000000031",
      },
      {
        avatarUrl: "/assets/fixture-table.svg",
        count: 1,
        displayName: "Rahul",
        recipientId: "00000000-0000-4000-8000-000000000032",
      },
    ],
    hasMore: false,
    id: SHARE_A,
    label: "Winter garden",
    loadingMore: false,
    ownerAvatarUrl: "/assets/fixture-garden.svg",
    ownerEmail: "meera@example.com",
    ownerUsername: "meera",
    originalMediaCount: 2,
    totalMediaCount: 4,
    media: [
      {
        aspectRatio: 4 / 3,
        canDownloadOriginal: true,
        createdAt: "2025-12-22T10:00:00.000Z",
        id: PHOTO_A,
        kind: "image" as const,
        label: "Green leaves in the winter garden",
        originalSizeLabel: "2.8 MB",
        source: "/assets/fixture-garden.svg",
      },
      {
        aspectRatio: 3 / 4,
        canDelete: true,
        canDownloadOriginal: true,
        createdAt: "2025-12-24T10:00:00.000Z",
        id: PHOTO_B,
        kind: "image" as const,
        label: "Afternoon table",
        originalSizeLabel: "1.6 MB",
        source: "/assets/fixture-table.svg",
        uploadedBy: {
          avatarUrl: "/assets/fixture-unsplash.jpg",
          displayName: "Anika",
          recipientId: "00000000-0000-4000-8000-000000000031",
        },
      },
      {
        aspectRatio: 1,
        canDownloadOriginal: true,
        createdAt: "2025-12-23T10:00:00.000Z",
        id: PHOTO_C,
        kind: "image" as const,
        label: "Sunlight through the window",
        originalSizeLabel: "2.1 MB",
        source: "/assets/fixture-window.svg",
        uploadedBy: {
          avatarUrl: "/assets/fixture-table.svg",
          displayName: "Rahul",
          recipientId: "00000000-0000-4000-8000-000000000032",
        },
      },
      {
        aspectRatio: 3 / 2,
        canDownloadOriginal: true,
        createdAt: "2025-12-21T10:00:00.000Z",
        durationLabel: "0:42",
        id: VIDEO,
        kind: "video" as const,
        label: "Walk along the coast",
        sizeLabel: "38.4 MB",
        source: "/assets/fixture-coast.svg",
      },
    ],
  },
  {
    access: "view" as const,
    canContribute: false,
    collaboratorFilter: "all",
    collaborators: [],
    hasMore: false,
    id: SHARE_B,
    label: "Family afternoon",
    loadingMore: false,
    ownerAvatarUrl: "/assets/fixture-coast.svg",
    ownerEmail: "rahul@example.com",
    ownerUsername: "rahul",
    originalMediaCount: 2,
    totalMediaCount: 2,
    media: [
      {
        aspectRatio: 3 / 4,
        createdAt: "2025-11-18T10:00:00.000Z",
        id: "00000000-0000-4000-8000-000000000024",
        kind: "image" as const,
        label: "A bright afternoon table",
        originalSizeLabel: "1.8 MB",
        source: "/assets/fixture-table.svg",
      },
      {
        aspectRatio: 4 / 3,
        createdAt: "2025-11-19T10:00:00.000Z",
        id: "00000000-0000-4000-8000-000000000025",
        kind: "image" as const,
        label: "The coast in clear weather",
        originalSizeLabel: "3.2 MB",
        source: "/assets/fixture-coast.svg",
      },
    ],
  },
] as const;

const admissionStates = new Set<ViewerAdmissionState>([
  "authorizing",
  "waiting-for-owner",
  "waiting-for-capacity",
  "connecting",
  "connected",
  "reconnecting",
  "unavailable",
]);

export function fixtureFromLocation(location: Location): ViewerUiModel | null {
  const name = new URLSearchParams(location.search).get("fixture");
  if (!name) return null;
  if (name === "invitation") return base({ mode: "invitation", shares: [] });
  if (name === "verification") {
    return base({
      destination: "Code sent to a••••@example.com",
      mode: "verification",
      shares: [],
    });
  }
  if (name === "opening-shell") {
    return base({
      admission: "connecting",
      openingShell: true,
      shares: [],
    });
  }
  if (name === "account-gallery") {
    return base({
      account: {
        avatarUrl: "/assets/fixture-unsplash.jpg",
        displayName: "Dove",
        username: "dove",
      },
      accountDashboardUrl: "/account",
    });
  }
  if (name === "album-downloading") {
    return base({
      albumDownload: {
        completed: 1,
        current: 2,
        failed: 0,
        progress: 0.42,
        state: "downloading",
        total: shares[0].media.length,
      },
    });
  }
  if (admissionStates.has(name as ViewerAdmissionState)) {
    const admission = name as ViewerAdmissionState;
    return base({
      admission,
      shares: admission === "connected" || admission === "reconnecting"
        ? shares
        : [],
    });
  }
  if (name === "revoked") {
    return base({
      revokedShareLabel: "Winter garden",
      selectedShareId: SHARE_B,
      shares: [shares[1]],
    });
  }
  if (name === "switcher") return base({ shareMenuOpen: true });
  if (name === "load-more") {
    return base({
      shares: [{
        ...shares[0],
        hasMore: true,
        totalMediaCount: 24,
      }, shares[1]],
    });
  }
  if (name === "unloaded-album-count") {
    return base({
      shares: [
        shares[0],
        {
          ...shares[1],
          media: [],
          originalMediaCount: 6,
          totalMediaCount: 6,
        },
      ],
    });
  }
  if (name === "loading") {
    return base({
      shares: [{
        ...shares[0],
        media: shares[0].media.map((media) => ({ ...media, source: undefined })),
      }, shares[1]],
    });
  }
  if (name === "photo-dialog") return base({ selectedMediaId: PHOTO_A });
  if (name === "webp-photo-dialog") {
    return base({
      selectedMediaId: PHOTO_A,
      shares: [{
        ...shares[0],
        media: shares[0].media.map((media) => (
          media.id === PHOTO_A ? { ...media, source: undefined } : media
        )),
      }, shares[1]],
    });
  }
  if (name === "photo-downloading") {
    return base({
      selectedMediaId: PHOTO_A,
      transferMediaId: PHOTO_A,
      transferProgress: 0.42,
      transferState: "transferring",
    });
  }
  const transfer = videoTransferState(name);
  if (name === "video-available" || transfer) {
    return base({
      selectedMediaId: VIDEO,
      transferProgress: transfer ? 0.42 : undefined,
      transferState: transfer,
      videoCapability: "testing",
    });
  }
  if (name === "video-disabled") {
    return base({
      announcement: "Video downloads are unavailable now. Your photos are still available.",
      shares: shares.map((share) => ({
        ...share,
        media: share.media.filter((media) => media.kind !== "video"),
      })),
      videoCapability: "disabled-during-session",
    });
  }
  if (name === "video-hidden") {
    return base({
      shares: shares.map((share) => ({
        ...share,
        media: share.media.filter((media) => media.kind !== "video"),
      })),
      videoCapability: "hidden",
    });
  }
  return base({});
}

function base(overrides: Partial<ViewerUiModel>): ViewerUiModel {
  return {
    admission: "connected",
    mediaSort: "added",
    mediaSortDirection: "ascending",
    mode: "gallery",
    selectedShareId: SHARE_A,
    shares,
    videoCapability: "testing",
    ...overrides,
  };
}

function videoTransferState(name: string): TransferState | undefined {
  const map: Record<string, TransferState> = {
    "video-cancelled": "cancelled",
    "video-completed": "completed",
    "video-failed": "failed",
    "video-queued": "queued",
    "video-transferring": "transferring",
    "video-verifying": "verifying",
  };
  return map[name];
}
